import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { apiUrl } from "../config";
import { decodeHtml } from "../config/functions";
import { ClientRegistrationType } from "../redux/actions/clientRegistration";
import { getSalesColumnSettings } from "./salesSettings";
import {
  BillPdfPrinterContext,
  getReceiptLogoImageUrl,
  getReceiptPrinterDisplayOptions,
  getReceiptQrImageUrl,
} from "./thermalSalesReceiptPrint";
export type SalesBillPrintObject = {
  billnumber?: string;
  date?: string;
  time?: string;
  placeOfSupply?: string | null;
  taxable?: string | number;
  cgst?: string | number;
  sgst?: string | number;
  previousBal?: string | number;
  credit?: string | number;
  finaltotal?: string | number;
  paymentamount?: string | number;
  paymentMode?: string;
  customer?: {
    name?: string;
    accountName?: string;
    place?: string;
    conNo?: string;
    contactNumber1?: string | number;
    contactNumber2?: string | number;
    conNo2?: string;
    gstNo?: string;
  };
  productitems?: Array<{
    name?: string;
    hsn?: string | number;
    qty?: string | number;
    rate?: string | number;
    gst?: string | number;
    total?: string | number;
  }>;
};

const DEFAULT_FOOTER =
  "Goods Once Sold Cannot be taken back. Thanks Come Again.";

function customerName(c: SalesBillPrintObject["customer"]): string {
  return decodeHtml(c?.name || c?.accountName || "");
}

function customerCell(c: SalesBillPrintObject["customer"]): string {
  return String(c?.conNo ?? c?.contactNumber1 ?? "");
}

function customerVehicle(c: SalesBillPrintObject["customer"]): string {
  return String(c?.conNo2 ?? c?.contactNumber2 ?? "");
}

function customerGst(c: SalesBillPrintObject["customer"]): string {
  return c?.gstNo || "NIL";
}

function imageFormatFromDataUrl(dataUrl: string): "JPEG" | "PNG" {
  return dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
}

async function fetchImageAsDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { method: "GET", mode: "cors", cache: "no-store" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve(typeof reader.result === "string" ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

async function tryAddImageFromUrl(
  doc: jsPDF,
  url: string | null,
  x: number,
  y: number,
  w: number,
  h: number,
): Promise<boolean> {
  if (!url) return false;
  const dataUrl = await fetchImageAsDataUrl(url);
  if (!dataUrl) return false;
  try {
    doc.addImage(dataUrl, imageFormatFromDataUrl(dataUrl), x, y, w, h);
    return true;
  } catch {
    return false;
  }
}

function tryAddDomImage(
  doc: jsPDF,
  elementId: string,
  x: number,
  y: number,
  w: number,
  h: number,
): boolean {
  if (typeof document === "undefined") return false;
  const img = document.getElementById(elementId) as HTMLImageElement | null;
  if (!(img instanceof HTMLImageElement) || !img.complete || !img.naturalWidth) {
    return false;
  }
  try {
    doc.addImage(img, "JPEG", x, y, w, h);
    return true;
  } catch {
    return false;
  }
}

/**
 * Laser/A4 sales invoice PDF — header/footer/logo/GST/QR driven by printer settings.
 */
export async function generateSalesBillPdf(
  printObj: SalesBillPrintObject,
  client: ClientRegistrationType | undefined,
  ctx: BillPdfPrinterContext,
): Promise<Blob> {
  const opts = getReceiptPrinterDisplayOptions(ctx.settings, ctx.branchId);
  const logoUrl = getReceiptLogoImageUrl(
    ctx.settings,
    ctx.branchId,
    client,
    ctx.apiBaseUrl ?? apiUrl ?? "",
  );

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  let y = 5;
  const marginLeft = 15;
  let textX = marginLeft;
  const logoW = 20;
  const logoH = 15;

  if (opts.showLogo) {
    const added = await tryAddImageFromUrl(
      doc,
      logoUrl,
      marginLeft,
      y,
      logoW,
      logoH,
    );
    if (added) {
      textX = marginLeft + logoW + 3;
    }
  }

  let lineY = y + 2.5;

  if (opts.showLogoText) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(client?.shopFullName || "", textX, lineY);
    lineY += 3.5;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(
    `${client?.address1 || ""}, ${client?.address2 || ""}`,
    textX,
    lineY,
  );
  lineY += 3.5;
  doc.text(
    `${client?.district || ""} - ${client?.pincode || ""}`,
    textX,
    lineY,
  );
  lineY += 3.5;
  doc.text("Cell: " + (client?.contactNo1 || ""), textX, lineY);

  lineY += 3.5;
  doc.text("GST No: " + (client?.gstNo || ""), textX, lineY);
  

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("SALES TAX INVOICE", 97, y + 9);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);

  const qrX = 180;
  if (opts.showQr) {
    const qrUrl = getReceiptQrImageUrl();
    const qrAdded =
      (await tryAddImageFromUrl(doc, qrUrl, qrX, y, 15, 15)) ||
      tryAddDomImage(doc, "phonepe", qrX, y, 15, 15);
    if (qrAdded) {
      doc.text("Gpay No: " + (client?.contactNo2 || ""), 166.5, y + 17.5);
    }
  }

  const headerBottom = Math.max(lineY + 2, y + 18.5);
  doc.line(marginLeft, headerBottom, 195, headerBottom);

  y = y + 15;

  doc.setFont("helvetica", "normal");
  doc.text(`Name : ${customerName(printObj.customer)}`, marginLeft, y + 6);
  doc.text(`Address : ${printObj.customer?.place || ""}`, marginLeft, y + 9);
  doc.text(`Cell No : ${customerCell(printObj.customer)}`, marginLeft, y + 12);
  if (opts.showGstNo) {
    doc.text(`GST No. : ${customerGst(printObj.customer)}`, marginLeft, y + 15);
  }

  doc.setFont("helvetica", "bold");
  doc.setFont("helvetica", "normal");
  doc.text(`Invoice No: ${printObj.billnumber || ""}`, 156.5, y + 6);
  doc.text(`Date: ${printObj.date || ""} ${printObj.time || ""}`, 156.5, y + 9);
  doc.text(`Vehicle No: ${customerVehicle(printObj.customer)}`, 156.5, y + 12);
  doc.text(`Payment: ${ctx.paymentLabel}`, 156.5, y + 15);

  doc.line(marginLeft, y + 16, 195, y + 16);

  const tableY = y + 16;
  const tableColumn = [
    "S.No",
    "Particulars",
    "HSN",
    "Qty",
    "Rate",
    "GST%",
    "Total",
  ];
  const tableRows =
    printObj?.productitems?.map((item, i) => [
      i + 1,
      item.name ?? "",
      item.hsn != null && String(item.hsn) !== "" ? String(item.hsn) : "-",
      item.qty ?? "",
      item.rate ?? "",
      item.gst ?? "",
      item.total ?? "",
    ]) || [];

  autoTable(doc, {
    startY: tableY,
    head: [tableColumn],
    body: tableRows,
    theme: "plain",
    styles: {
      fontSize: 8,
      cellPadding: 0.3,
      valign: "middle",
      lineWidth: 0,
    },
    headStyles: {
      textColor: 0,
      fontStyle: "bold",
      halign: "left",
    },
    columnStyles: {
      0: { halign: "center" },
      1: { halign: "left" },
      2: { halign: "left" },
      3: { halign: "center" },
      4: { halign: "right" },
      5: { halign: "center" },
      6: { halign: "right" },
    },
    margin: { left: marginLeft, right: 15 },
    didParseCell(data) {
      if (
        data.section === "head" &&
        (data.column.index === 4 || data.column.index === 6)
      ) {
        data.cell.styles.halign = "right";
      }
      if (
        data.section === "head" &&
        (data.column.index === 3 || data.column.index === 5)
      ) {
        data.cell.styles.halign = "center";
      }
    },
    didDrawPage() {
      if ((doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable) {
        const table = (doc as jsPDF & { lastAutoTable: { finalY: number } })
          .lastAutoTable;
        doc.setDrawColor(0);
        doc.setLineWidth(0.2);
        doc.line(marginLeft, table.finalY, 195, table.finalY);
      }
    },
    didDrawCell(data) {
      if (data.section === "head" && data.row.index === 0) {
        const { x, y: cy, width, height } = data.cell;
        data.doc.setDrawColor(0);
        data.doc.setLineWidth(0.2);
        data.doc.line(x, cy + height, x + width, cy + height);
      }
    },
  });

  const finalY = (doc as jsPDF & { lastAutoTable: { finalY: number } })
    .lastAutoTable.finalY;
  doc.line(marginLeft, finalY, 195, finalY);

  const commonY = finalY + 2;
  const leftTableX = 14;
  const rightTableX = 110;
  const showPaymentParticulars = getSalesColumnSettings(ctx.settings, ctx.branchId).showPaymentParticulars;
  const summaryHead = ["TAXABLE", "CGST", "SGST"];
  const summaryRow = [
    parseFloat(String(printObj.taxable || "0")).toFixed(2),
    String(printObj.cgst || "0"),
    String(printObj.sgst || "0"),
  ];

  if (showPaymentParticulars) {
    summaryHead.push("OLD BALANCE");
    summaryRow.push(`${printObj.previousBal || "0"}.00`);
  }

  autoTable(doc, {
    startY: commonY + 1,
    margin: { left: leftTableX + 5, top: 0, bottom: 0 },
    head: [summaryHead],
    body: [summaryRow],
    theme: "plain",
    styles: { fontSize: 8, halign: "center", cellPadding: 0.3 },
    tableLineWidth: 0,
    columnStyles: showPaymentParticulars
      ? {
          0: { cellWidth: 30 },
          1: { cellWidth: 30 },
          2: { cellWidth: 30 },
          3: { cellWidth: 30 },
        }
      : {
          0: { cellWidth: 40 },
          1: { cellWidth: 40 },
          2: { cellWidth: 40 },
        },
  });

  const paidNum = parseFloat(String(printObj.paymentamount ?? ""));
  const billNum = parseFloat(String(printObj.finaltotal || "0"));
  const paid = !Number.isNaN(paidNum) ? paidNum.toFixed(2) : billNum.toFixed(2);
  const balance = parseFloat(String(printObj.credit || "0")).toFixed(2);

  autoTable(doc, {
    startY: commonY - 1,
    margin: { left: rightTableX + 20, top: 0, bottom: 0 },
    body: [
      ["Bill Amount", ":", parseFloat(String(printObj.finaltotal || "0")).toFixed(2)],
      ["Amount Paid", ":", paid],
      ["Balance", ":", balance],
    ],
    theme: "plain",
    styles: { fontSize: 8, cellPadding: 0.3 },
    tableLineWidth: 0,
    columnStyles: {
      0: { fontStyle: "bold", halign: "right", cellWidth: 35 },
      1: { halign: "right", cellWidth: 5 },
      2: { halign: "right", cellWidth: 25 },
    },
  });

  const summaryLineY = (doc as jsPDF & { lastAutoTable: { finalY: number } })
    .lastAutoTable.finalY;
  doc.line(marginLeft, summaryLineY, 195, summaryLineY);

  const footerText = opts.footerMsg.trim() || DEFAULT_FOOTER;
  const pageWidth = doc.internal.pageSize.getWidth();
  const footerY = finalY + 20;
  doc.setFontSize(8);
  doc.text(footerText, pageWidth / 2, footerY, { align: "center" });

  return doc.output("blob");
}
